import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone, Send, Globe } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const SERVICES = [
  "Remote Office Administration",
  "Customer Support",
  "Bookkeeping Support",
  "Trucking Office Administration",
  "HR & Payroll",
  "Document Management",
  "Weekly Office Visits (GTA)",
  "Not Sure Yet",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply within 1 business day.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-28 lg:py-36 bg-[#050E1A] text-white overflow-hidden" data-testid="contact-section">
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#16A34A]/20 blur-3xl pointer-events-none" />
      <div className="container-apex relative">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline">Contact</div>
            <h2 className="heading-display text-4xl sm:text-5xl lg:text-6xl mt-5">
              Let's quiet your inbox.
            </h2>
            <p className="mt-6 text-white/70 text-lg leading-relaxed max-w-md">
              Tell us a bit about your business. We'll respond within one business day with a tailored next step.
            </p>

            <div className="mt-12 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#16A34A]"><MapPin size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Based in</div>
                  <div className="font-semibold mt-1">Brampton, Ontario · Serving all of Canada</div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#16A34A]"><Mail size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Email</div>
                  <a href="mailto:info@adminapex.ca" className="font-semibold hover:text-[#16A34A] transition mt-1 block">info@adminapex.ca</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#16A34A]"><Phone size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Phone</div>
                  <a href="tel:+16475107379" data-testid="contact-phone" className="font-semibold hover:text-[#16A34A] transition mt-1 block">+1 (647) 510-7379</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-[#16A34A]"><Globe size={18} /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/50 font-bold">Web</div>
                  <div className="font-semibold mt-1">www.adminapex.ca</div>
                </div>
              </div>
            </div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            onSubmit={submit}
            className="lg:col-span-7 rounded-3xl bg-white/5 border border-white/10 backdrop-blur-xl p-8 lg:p-10"
            data-testid="contact-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" id="c-name" required value={form.name} onChange={set("name")} />
              <Field label="Email" id="c-email" type="email" required value={form.email} onChange={set("email")} />
              <Field label="Phone" id="c-phone" value={form.phone} onChange={set("phone")} />
              <Field label="Company" id="c-company" value={form.company} onChange={set("company")} />
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2 block">Service of interest</label>
                <select
                  value={form.service}
                  onChange={set("service")}
                  data-testid="contact-service"
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30"
                >
                  <option value="" className="text-black">Select a service…</option>
                  {SERVICES.map((s) => <option key={s} value={s} className="text-black">{s}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2 block">How can we help?</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={set("message")}
                  data-testid="contact-message"
                  className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30"
                  placeholder="Tell us about your business and what you need help with…"
                />
              </div>
            </div>
            <div className="mt-8 flex items-center justify-between flex-wrap gap-4">
              <div className="text-xs text-white/50">We respond within 1 business day. NDA available on request.</div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-accent">
                {loading ? "Sending…" : sent ? "Sent ✓" : "Send Message"} {!loading && <Send size={16} />}
              </button>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, id, type = "text", value, onChange, required }) {
  return (
    <div>
      <label htmlFor={id} className="text-xs uppercase tracking-widest text-white/60 font-bold mb-2 block">{label}</label>
      <input
        id={id}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={id}
        className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#16A34A] focus:ring-2 focus:ring-[#16A34A]/30"
      />
    </div>
  );
}
